import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { DBTimelineThunks, timelineSelectOneObjectByQuery, TimelineEventReducerActions } from '../../../redux/thunks/DBServiceThunks';
import { DBTimelineEventThunks, timelineEventSelectByQuery } from '../../../redux/thunks/DBServiceThunks';
import TimelineEmptyState from './timeline-empty-state';
import TimelineProgress from './timeline-progress';
import TimelineUpdateBanner from './timeline-update-banner';
import TimelineDateGroup from './timeline-date-group';
import NoKnowledgebaseState from '../no-knowledgebase-state';
import { createSelector } from '@reduxjs/toolkit';
import { groupEventsByDate, GroupedTimelineEvents } from '../../../utils/timeline-date-utils';
import EventAddDialog from './event-add-dialog';
import { selectCanEditCurrentCase } from '../../../redux/selectors/knowledgebaseSelectors';

// Memoized selectors
const selectTimeline = createSelector(
  [(state) => state.DBTimelineReducer, (state) => state.CaseReducer.currentCaseId, (_, knowledgebaseId) => knowledgebaseId],
  (timelineReducer, currentCaseId, knowledgebaseId) =>
    timelineSelectOneObjectByQuery(timelineReducer, { knowledgebaseId })
);

const selectTimelineEvents = createSelector(
  [(state) => state.DBTimelineEventReducer, (_, timelineId) => timelineId],
  (timelineEventReducer, timelineId) =>
    timelineId ? timelineEventSelectByQuery(timelineEventReducer, { timelineId }) : []
);

export default function TimelineView() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);

  // Check if user can edit
  const canEdit = useAppSelector(selectCanEditCurrentCase);

  // Get knowledgebase for current case
  const knowledgebase = useAppSelector(state =>
    state.DBKnowledgebaseReducer.ids
      .map(id => state.DBKnowledgebaseReducer.entities[id])
      .find(kb => kb?.caseId === currentCaseId)
  );

  const knowledgebaseId = knowledgebase?._id;

  // Get timeline for this knowledgebase
  const timeline = useAppSelector(state =>
    knowledgebaseId ? selectTimeline(state, knowledgebaseId) : null
  );

  // Get timeline events from entities
  const timelineEventEntities = useAppSelector(state => state.DBTimelineEventReducer.entities);
  const timelineEvents = useMemo(() => {
    if (!timeline?._id) return [];
    return Object.values(timelineEventEntities).filter(
      (event): event is NonNullable<typeof event> => event?.timelineId === timeline._id
    );
  }, [timeline?._id, timelineEventEntities]);

  const [isLoading, setIsLoading] = useState(false);
  const lastFetchedKbIdRef = useRef<string | null>(null);
  const lastFetchedTimelineIdRef = useRef<string | null>(null);

  // Lazy loading state
  const BATCH_SIZE = 20;
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Fetch timeline when knowledgebase changes
  useEffect(() => {
    if (knowledgebaseId && knowledgebaseId !== lastFetchedKbIdRef.current) {
      lastFetchedKbIdRef.current = knowledgebaseId;
      setIsLoading(true);
      dispatch(DBTimelineThunks.find({ knowledgebaseId }))
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
    }
  }, [knowledgebaseId, dispatch]);

  // Fetch timeline events when timeline is available (initial batch with pagination)
  useEffect(() => {
    const timelineId = timeline?._id;
    if (timelineId && timelineId !== lastFetchedTimelineIdRef.current) {
      lastFetchedTimelineIdRef.current = timelineId;

      // Clear previous timeline events from state
      dispatch(TimelineEventReducerActions.removeAll());

      // Reset pagination state
      setHasMoreEvents(true);
      setIsLoadingMore(false);

      setIsLoading(true);
      dispatch(DBTimelineEventThunks.find({
        timelineId,
        $limit: BATCH_SIZE,
        $sort: { startDate: -1 } // Newest first
      }))
        .then((result) => {
          const fetchedEvents = result.payload || [];
          setHasMoreEvents(fetchedEvents.length === BATCH_SIZE);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [timeline?._id, dispatch]);

  // Load more events (lazy loading)
  const loadMoreEvents = useCallback(async () => {
    if (isLoadingMore || !hasMoreEvents || !timeline?._id || !scrollContainerRef.current) return;

    // Save current scroll position before loading more
    const scrollContainer = scrollContainerRef.current;
    const previousScrollHeight = scrollContainer.scrollHeight;
    const previousScrollTop = scrollContainer.scrollTop;

    setIsLoadingMore(true);
    try {
      // Get current events count for this timeline
      const currentEventsCount = timelineEvents.length;

      const result = await dispatch(DBTimelineEventThunks.find({
        timelineId: timeline._id,
        $limit: BATCH_SIZE,
        $skip: currentEventsCount,
        $sort: { startDate: -1 }
      }));

      const newEvents = result.payload || [];
      setHasMoreEvents(newEvents.length === BATCH_SIZE);

      // Restore scroll position after new events are added at the bottom
      setTimeout(() => {
        if (scrollContainer) {
          const newScrollHeight = scrollContainer.scrollHeight;
          const heightDifference = newScrollHeight - previousScrollHeight;
          // Keep scroll position stable (don't jump)
          scrollContainer.scrollTop = previousScrollTop;
        }
      }, 0);
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreEvents, timeline?._id, timelineEvents.length, dispatch]);

  // IntersectionObserver to trigger lazy loading when 2nd-to-last event is visible
  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    // Wait for next frame to ensure DOM is rendered
    const timeoutId = setTimeout(() => {
      const triggerElement = loadMoreTriggerRef.current;

      if (!triggerElement || !hasMoreEvents || isLoadingMore) return;

      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreEvents();
          }
        },
        {
          root: scrollContainerRef.current,
          threshold: 0.1
        }
      );

      observer.observe(triggerElement);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [hasMoreEvents, isLoadingMore, timelineEvents.length, loadMoreEvents]);

  // Sort events by date (most recent first)
  const sortedEvents = useMemo(() => {
    return [...timelineEvents].sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);

      // If dates are the same, sort by time if available
      if (dateA.getTime() === dateB.getTime() && a.startTime && b.startTime) {
        return b.startTime.localeCompare(a.startTime);
      }

      return dateB.getTime() - dateA.getTime();
    });
  }, [timelineEvents]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    return groupEventsByDate(sortedEvents);
  }, [sortedEvents]);

  // Helper to determine if an event is the last in the entire timeline
  const getIsLastEvent = useCallback((eventId: string, groupIndex: number): boolean => {
    if (groupIndex === groupedEvents.length - 1) {
      const lastGroup = groupedEvents[groupedEvents.length - 1];
      return lastGroup.events[lastGroup.events.length - 1]?._id === eventId;
    }
    return false;
  }, [groupedEvents]);

  // No case selected - prompt user to select a case
  if (!currentCaseId) {
    return <NoKnowledgebaseState />;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  // No knowledgebase for this case
  if (!knowledgebase) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <p className="text-sm text-muted-foreground text-center">
          {t('common.timeline.noKnowledgebase')}
        </p>
      </div>
    );
  }

  // No timeline generated yet
  if (!timeline) {
    return <TimelineEmptyState knowledgebaseId={knowledgebaseId!} />;
  }

  // Timeline is generating - show progress
  if (timeline.status === 'generating') {
    return <TimelineProgress timeline={timeline} onComplete={() => {
      // Refetch timeline and events when complete
      dispatch(DBTimelineThunks.find({ knowledgebaseId }));
      if (timeline._id) {
        // Clear and refetch events with pagination
        dispatch(TimelineEventReducerActions.removeAll());
        setHasMoreEvents(true);
        setIsLoadingMore(false);
        dispatch(DBTimelineEventThunks.find({
          timelineId: timeline._id,
          $limit: BATCH_SIZE,
          $sort: { startDate: -1 }
        }));
      }
    }} />;
  }

  // Timeline exists but no events
  if (sortedEvents.length === 0) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Update Banner */}
        {timeline.needsUpdate && <TimelineUpdateBanner timeline={timeline} />}

        {/* Empty state message */}
        <div className="flex flex-col items-center justify-center flex-1 p-6 gap-4">
          <p className="text-sm text-muted-foreground text-center">
            {t('common.timeline.noEvents')}
          </p>
          <Button onClick={() => setShowAddDialog(true)} size="sm" disabled={!canEdit}>
            <Plus className="h-4 w-4 mr-2" />
            {t('common.timeline.addEvent')}
          </Button>
        </div>

        <EventAddDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          timelineId={timeline._id}
          knowledgebaseId={knowledgebaseId!}
          caseId={currentCaseId!}
          ownerId={timeline.ownerId}
        />
      </div>
    );
  }

  // Display timeline events
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Update Banner */}
      {timeline.needsUpdate && <TimelineUpdateBanner timeline={timeline} />}

      {/* Add Event Button - Fixed at top */}
      <div className="flex-shrink-0 px-2 py-2 border-b border-border">
        <Button onClick={() => setShowAddDialog(true)} size="sm" variant="outline" className="w-full" disabled={!canEdit}>
          <Plus className="h-4 w-4 mr-2" />
          {t('common.timeline.addEvent')}
        </Button>
      </div>

      {/* Timeline Events - Scrollable with Date Grouping */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {groupedEvents.map((group, groupIndex) => {
            // Attach ref to 2nd-to-last group to trigger loading when scrolling down
            const is2ndFromBottom = groupIndex === groupedEvents.length - 2;
            const isLastGroup = groupIndex === groupedEvents.length - 1;

            return (
              <div
                key={group.dateKey}
                ref={is2ndFromBottom ? loadMoreTriggerRef : undefined}
              >
                <TimelineDateGroup
                  dateLabel={group.dateLabel}
                  events={group.events}
                  getIsLastEvent={getIsLastEvent}
                  groupIndex={groupIndex}
                />
              </div>
            );
          })}

          {/* Lazy loading indicator at bottom */}
          {hasMoreEvents && sortedEvents.length >= 2 && isLoadingMore && (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-gold" />
            </div>
          )}
        </div>
      </div>

      <EventAddDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        timelineId={timeline._id}
        knowledgebaseId={knowledgebaseId!}
        caseId={currentCaseId!}
        ownerId={timeline.ownerId}
      />
    </div>
  );
}

